import { useConnectionEndpoint } from "../../connectionAnalyser/useConnectionEndpoint";
import { observeNavigatorGrabHover, isNavigatorRowGrab } from "../../connectionAnalyser/navigatorGrabHover";
/**
 * NavigationTreeNode.tsx
 *
 * Renders a single node (row) in the KNX navigation tree.
 * Responsibilities:
 *  - Displays the object's icon, tag, name, prefix/postfix, and action buttons.
 *  - Handles expand/collapse of child nodes via the `NavigationTreeNodes` sub-component.
 *  - Manages focus/blur interactions for inline editing of tag and name fields.
 *  - Shows context menus, drag-and-drop, modified-status indicators, error badges,
 *    and project-compare status markers.
 *  - Supports both the main navigator tree and the global-selection ("commander") tree.
 */
import React, { useState, useRef, useEffect } from "react";
import { useDeletedNodeAnimation } from "../useDeletedNodeAnimation";

import { withErrorBoundary } from "react-error-boundary";

import "./style.scss";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import NavigationTreeNodes from "./NavigationTreeNodes";
import { treeViewNodeId } from ".";
import EditableText from "../EditableText";
import TreeNodeInfo from "../../models/TreeNodeInfo";
import { showToast } from "../Toasts";

import Logger from "../../Logger";
import { useRedraw } from "../../models/useRerender";
import Browser from "../../browser";
import { TreeContext } from "./TreeContext";
import { panelResizeActive } from "../PanelResizer";

import { isDevelopmentEnvironment } from "../../estada-config";
import { enumCompareStatus } from "../../models/BaseObject";
import { itx, noItx } from "../../translationItx/itx";
import ErrorItems from "../../models/ErrorItems";
import BaseModel from "../../models/BaseModel";
import { ApplicationMode } from "../../models/SettingsEnums";
import type BaseModelReference from "../../models/BaseModelReference";
import type Room from "../../models/Room";
import ErrorFallback from "../ErrorFallback";
import TreeMenuIcon from "./TreeMenuIcon";
import NavigatorBranchButton, { secondaryNavigatorTarget } from "./NavigatorBranchButton";
import TreeMenuPanel from "./TreeMenuPanel";
import { showTreeMenu, hideTreeMenu } from "./treeMenuBehavior";
import BaseDialog from "../../modelDialogs/BaseDialog";
import ObjectReference from "../ObjectReference";
import { ShowErrorsToUsersOutsideOfDevelopmentElement } from "../dialogs/ShowErrorsToUsersOutsideOfDevelopment";
import { isUiModeDeveloper } from "../../uiMode";
import { navigatorRows, type NavigatorNode } from "./NavigatorModel";
import { zoomC1NavigatorObject } from "./c1NavigatorZoom";

const logger = new Logger();

/**
 * Wraps NavigationTreeNodeContent in an error boundary so that a single broken node
 * does not crash the entire navigation tree.
 */
const NavigationTreeNodeContentWithErrorBoundary = withErrorBoundary(NavigationTreeNodeContent, {
  FallbackComponent: ErrorFallback,
  onError: (error, info) => {
    console.error("Caught an react error:", error, info);
  },
  resetKeys: ["R"],
});

/**
 * Positions the flyout action-button menu (`treeMenuButtonsWrapper`) so that it appears
 * flush with the right edge of the navigation tree scroller.
 * Also auto-closes the menu if the corresponding row has scrolled above the visible area.
 */
export function adjustMenu(menu: HTMLElement | null | undefined) {
  try {
    if (menu) {
      const navigationTreeScroller =
        menu.closest(".navigationTreeScroller") ?? document.getElementById("navigationTreeScroller");
      if (navigationTreeScroller) {
        // Anchor before the scrollbar so the row and its flyout form one continuous hover area.
        const bounds = navigationTreeScroller.getBoundingClientRect();
        const w = bounds.left + navigationTreeScroller.clientLeft + navigationTreeScroller.clientWidth - 5;
        const top = menu.parentElement?.getBoundingClientRect().top;

        (menu as HTMLElement).style.left = `${w}px`;
        (menu as HTMLElement).style.top = `${top}px`;

        //
        // close the menu / blur focus of focued Object,
        // if the object is not is not full visible,
        // because is is scrolled out of to top of the view
        //

        const dataline = menu.closest(".dataline") as HTMLElement; // no itx
        if (dataline.contains(document.activeElement)) {
          const topScroller = navigationTreeScroller?.getBoundingClientRect().top;
          if (top && topScroller && top < topScroller) {
            dataline.blur();
          }
        }
      }
    }
  } catch (ex) {
    logger.error(ex);
  }
}

/**
 * When the user focuses away from a node that was opened in "transient" mode
 * (i.e., opened temporarily by hover/selection), this converts the pane to
 * a fixed (permanent) open state so it stays visible.
 * For Room objects the linked page pane is targeted instead of the room itself.
 */
function setFixedOpenIfIsTransientOpen(obj: BaseModel) {
  let id = obj.id;
  if (obj.className == "Room") {
    id = (obj as Room).page?.id || "";
  }

  if (state.activePanes.isOpen(id)) {
    const pane = state.activePanes.getPane(id);
    if (pane?.isTransitOpen) {
      pane.isTransitOpen = false;
      pane.tabItemData?.raiseOnChange("isTransitOpen");
    }
  }
}

/**
 * Core render component for a single tree node.
 * Receives the domain model node, nesting depth, expand/collapse state,
 * and tree context, then builds the full row UI.
 */
function NavigationTreeNodeContent(props: {
  nesting: number;
  node: TreeNodeInfo;
  parentNodeInfo: TreeNodeInfo;
  isExpanded: boolean;
  navigatorNode?: NavigatorNode;
  setIsExpanded: (newValue: boolean) => void;
  treeContext: TreeContext;
  allignAllColumns?: (parentObj: BaseModel, elm?: HTMLElement | null) => void;
  flyoutMenus: boolean;
}) {
  const { node, nesting, isExpanded, setIsExpanded, treeContext, allignAllColumns, flyoutMenus } = props;
  const redraw = useRedraw();

  // Incremented on every render to generate unique test-IDs for child elements.
  const counter = useRef(0);
  // Blocks inline editing for a short time after focus arrives (avoids accidental edits).
  const focusDelayPending = useRef<boolean>(true);

  // Root DOM element of this tree row — used for class manipulation and position queries.
  const elmRef = useRef<HTMLDivElement>(null);
  // Tracks whether the pointer is inside the flyout button dialog to keep it visible on hover.
  const mouseIsInsideButtonsDialog = useRef(false);

  // The SVG grab lives outside the row DOM but belongs to the same hover region.
  useEffect(() => {
    const row = elmRef.current;
    if (!row) return;
    return observeNavigatorGrabHover(row, onMouseEnterContent, onMouseLeaveOnContent);
  }, []);

  const obj = node.obj;
  const isReferenceObject = ["BaseModelReference", "DeviceReference", "FuseConsumerReference"].includes(obj.className);
  const referencedObject =
    node.referencedObj ?? (isReferenceObject ? (obj as BaseModelReference).referencedObject : undefined);
  useConnectionEndpoint(
    state.connectionAnalyser,
    referencedObject ?? obj,
    elmRef,
    "other", // no itx
    treeContext.treename !== "navigator",
  );
  // Handles synthetic "expand" DOM events dispatched programmatically from outside React.
  function handleExpandCollapseEvent(ev: any) {
    setIsExpanded(ev.detail.action == "expand");
  }

  // On mount: mark the first action button and attach the custom "expand" event listener.
  // On unmount: remove the event listener to avoid memory leaks.
  useEffect(() => {
    const firstButton = firstMenuButton();
    if (firstButton) {
      firstButton.classList.add("firstButton"); // no itx
    }

    if (elmRef.current) {
      if (props.navigatorNode) navigatorRows.set(elmRef.current, props.navigatorNode);
      else elmRef.current.addEventListener("expand", handleExpandCollapseEvent);
    }

    return () => {
      if (elmRef.current) {
        navigatorRows.delete(elmRef.current);
        elmRef.current.removeEventListener("expand", handleExpandCollapseEvent);
      }
    };
  }, []);

  /**
   * Inner component that renders the flyout or inline action-button menu for this node.
   * - Collects and groups child-object errors only when the error button is clicked.
   * - Adds an error-triangle button if errors exist.
   * - In the global-selection tree, renders buttons inline without a flyout wrapper.
   * - In the normal tree with flyoutMenus enabled, wraps buttons in a fixed-position
   *   `<dialog>` so they overlay the tree without affecting layout.
   */
  function ButtonsDialog(params: { node: TreeNodeInfo; obj: BaseModel; treeContext?: TreeContext }): any {
    const { obj, treeContext } = params;

    const menuWrapperRef = useRef<HTMLDivElement>(null);

    // Gather all objects in the subtree that have errors.
    const allErrorObjects = obj.findAllErrorInChildObjects();
    // Map error message → affected objects, for grouped display in the error dialog.
    const toShowErrors = new Map<string, Array<BaseModel>>();

    state.consistencyCheckLog.forEach((err) => {
      const obj = state.objectRegistry.getObject(err.id) as BaseModel;
      if (obj && allErrorObjects.includes(obj)) {
        const userErrorMessage = isUiModeDeveloper() ? err.message || err.userError || "" : err.userError || "";
        if (userErrorMessage && userErrorMessage != "" && obj) {
          const errorSet = toShowErrors.get(userErrorMessage) || [];
          errorSet.push(obj);
          toShowErrors.set(userErrorMessage, errorSet);
        }
      }
    });

    // Highlights the row when the pointer enters the floating button dialog.
    // Skipped while a panel resize is in progress to avoid visual glitches.
    function onMouseEnterButtonsDialog(e: any) {
      if (panelResizeActive) {
        return;
      }
      if (elmRef.current) {
        elmRef.current.classList.add("firstButtonActive");
        elmRef.current.classList.remove("firstButtonInactive");
      }

      mouseIsInsideButtonsDialog.current = true;
    }

    // Removes the hover highlight when the pointer leaves the button dialog.
    function onMouseLeaveButtonsDialog(e: any) {
      mouseIsInsideButtonsDialog.current = false;

      if (elmRef.current) {
        elmRef.current?.classList.remove("firstButtonActive");
      }
    }

    function onMouseMoveButtonsDialog(e: any) {}

    function showChildErrors() {
      // Gather all objects in the subtree that have errors.
      const allErrorObjects = obj.findAllErrorInChildObjects();
      // Map error message → affected objects, for grouped display in the error dialog.
      const toShowErrors = new Map<string, Array<BaseModel>>();

      // Build the map directly from each object's own `errors` (the same source that
      // drives `hasErrors`/`errorInChild`), instead of `state.consistencyCheckLog`,
      // which is only populated by an explicit `state.consistencyCheck()` run and would
      // otherwise leave this list empty even though the error icon is shown.
      allErrorObjects.forEach((obj1) => {
        for (const key in obj1.errors) {
          const message = obj1.errors[key as keyof ErrorItems];
          if (message || message === "") {
            const userErrorMessage = itx("errors." + key, key);
            const errorSet = toShowErrors.get(userErrorMessage) || [];
            errorSet.push(obj1);
            toShowErrors.set(userErrorMessage, errorSet);
          }
        }
      });

      if (toShowErrors.size > 0) {
        state.dialogManager.openDialog(
          <BaseDialog title="Fehlerliste">
            <ShowErrorsToUsersOutsideOfDevelopmentElement groupedErrors={toShowErrors} />
          </BaseDialog>,
          false,
        );
      } else {
        showToast({ text: itx("NavigationTreeNode.noErrors", "Keine Fehler vorhanden") });
      }
    }

    // Retrieve the action buttons defined by the domain object's tree view model.
    const includeProperties = !obj.isRoom ||
      (treeContext?.navigatorInstance !== "C1Groups" && treeContext?.navigatorInstance !== "C1Summary");
    let treeViewModelButtons = obj.treeViewModel.getButtonsWithFlyout(node, includeProperties);
    const isC1Navigator = treeContext?.navigatorInstance === "C1Groups" ||
      treeContext?.navigatorInstance === "C1Summary";
    if (isC1Navigator && state.c1.editMode.value !== "EloConcept" && state.c1.editMode.value !== "EloDetail") {
      treeViewModelButtons = treeViewModelButtons?.filter(button => button.props.iconName !== "plus-circle");
    }

    const secondaryTarget = treeContext?.onOpenSecondaryNavigator ? secondaryNavigatorTarget(obj) : undefined;
    if (secondaryTarget && treeContext?.onOpenSecondaryNavigator) {
      treeViewModelButtons = [...(treeViewModelButtons ?? []),
        <NavigatorBranchButton key="open-secondary-navigator" target={secondaryTarget} onOpen={treeContext.onOpenSecondaryNavigator} />];
    }

    // Append an error-indicator button when child-object errors are present.
    if (obj.errorInChild) {
      treeViewModelButtons ||= [];
      treeViewModelButtons.push(
        <TreeMenuIcon
          tooltipText={itx("NavigationTreeNode.errors", "Fehlerliste")}
          onClick={() => {
            showChildErrors();
          }}
          style={{ color: "red" }}
          iconName="exclamation-triangle" // no itx
          objName={"Errors"}
          key={"errors"}
        />,
      );
    }

    // In the global-selection (commander) tree, buttons are always shown inline
    // without a flyout wrapper, and a navigation button may be added if missing.
    if (treeContext?.isGlobalSelectionTree) {
      let hasNavButton = false;

      treeViewModelButtons?.forEach((button) => {
        try {
          hasNavButton ||= (button.key as string)?.indexOf("inlineButton_nav1") >= 0;
        } catch (ex) {}
      });

      return (
        <div className="treeMenuButtonsInner noFlyoutMenus" style={{ position: "absolute", right: "0" }}>
          {treeViewModelButtons}
        </div>
      );
    }

    if (!treeViewModelButtons?.length) {
      return null;
    }

    if (!flyoutMenus) {
      return (
        <div className="treeMenuButtonsInner noFlyoutMenus" style={{ position: "absolute", right: "0" }}>
          {treeViewModelButtons}
        </div>
      );
    }

    return (
      <div ref={menuWrapperRef} className="treeMenuButtonsWrapper" style={{ position: "fixed", left: "0px" }}>
        <TreeMenuPanel
          className={obj.selected ? "selected" : ""}
          onMouseEnter={onMouseEnterButtonsDialog}
          onMouseLeave={onMouseLeaveButtonsDialog}
          onMouseMove={onMouseMoveButtonsDialog}
          style={{ pointerEvents: "auto", zIndex: "20" }}
          trailing={
            !treeContext?.isGlobalSelectionTree && (
              <div
                className="clickFirstButtonLeft"
                onMouseDown={(event) => { if (event.button === 0) event.preventDefault(); }}
                onMouseEnter={onMouseEnterFirstMenuButton}
                onMouseLeave={onMouseLeaveFirstMenuButton}
                onClick={onClickFirstMenuButton}
              />
            )
          }
        >
          {treeViewModelButtons}
        </TreeMenuPanel>
      </div>
    );
  }

  // After every render: re-position the flyout button menu and
  // update the global flyout panel position when this node is selected.
  useEffect(() => {
    try {
      if (!treeContext?.isGlobalSelectionTree) {
        adjustMenu(elmRef.current?.querySelector(".treeMenuButtonsWrapper")); // no itx

        if (obj.selected) {
          const flyoutTreeviewMenu = document.getElementById("flyoutTreeviewMenu");

          const r = elmRef.current?.getBoundingClientRect();

          if (r && flyoutTreeviewMenu) {
            flyoutTreeviewMenu.style.left = `${r.right}px`;
            flyoutTreeviewMenu.style.top = `${r.top}px`;
          }
        }
      }
    } catch (ex) {}
  });

  // Re-render when the global "show modified status" flag or UI mode changes.
  useOnChangeWatcher(state._showModifiedStatus);
  useOnChangeWatcher(state.uiMode);
  useOnChangeWatcher(state.projectMetadata);
  useOnChangeWatcher(state.projectMetadata.isC1Project ? state.c1.editMode : undefined);
  // Re-render whenever the UI language changes, so labels/tags computed via itx() refresh immediately.
  useOnChangeWatcher(state.languageVersion);

  // React to property changes on the domain object itself:
  // - "selected" → redraw to update highlight
  // - "collapsed" / "expanded" → toggle the expand state
  // - "childCollection" → collapse if no children remain
  // Changes originating from the opposite tree (navigator ↔ commander) are ignored
  // for reference nodes to prevent redundant expand/scroll operations.
  useOnChangeWatcher(obj, (changedProperty, changedObject, source) => {
    // logger.log(
    //   `mwe1 useOnChangeWatcher ${obj.name} ${changedProperty} ${changedObject?.className}`,
    // );

    if (changedProperty == "selected") {
      redraw();
    }

    //
    // ReferenceNode dont automaticly expand or scrollTo
    //

    const s = treeContext?.isGlobalSelectionTree ? "commander" : "navigator"; // no itx
    if (source && source != s) {
      //
      // the expand comand comes from navigator to commander or visa versa,
      // ignore it
      return false;
    }

    if (node.isReference) {
      return true;
    }

    if (changedProperty == "collapsed") {
      setIsExpanded(false);
    } else if (changedProperty == "expanded") {
      logger.log(`mwe1 expandParentHirachie: expanded ${obj?.className}`);

      setIsExpanded(true);
    } else if (changedProperty == "childCollection") {
      if (!obj.treeViewModel.hasChildren) {
        logger.log(`setIsExpanded false ${obj.name}`);
        setIsExpanded(false);
      }
    }

    return true;
  });

  useOnChangeWatcher(referencedObject, (changedProperty) => {
    if (referencedObject) {
      obj.raiseOnChange(changedProperty);
    }
    return false;
  });

  // Initiates a drag operation. Shift/Ctrl forces copy-only mode; otherwise copy-or-move.
  function dragStart(e: any) {
    const mode = e.shiftKey || e.ctrlKey ? "copy" : "copyMove"; // no itx

    obj.treeViewModel.dragStart(e, mode);
  }

  // logger.log(`render NavigationTreeNode  ${obj.name} ${obj.selected}`);

  // var url: string | undefined = undefined;
  // url = obj.treeViewModel?.iconUrl;

  ///////////////////////////////////////////////////////////////

  // The icon element provided by the tree view model (e.g., a line-awesome <i> or an <img>).
  const icon = obj.treeViewModel?.icon;

  // if (!icon) {
  //   if (url?.substring(0, 2) == "la") {
  //     icon = (
  //       <div className="icon">
  //         <i className={url} onDoubleClick={onDoubleClick} onContextMenu={onContextMenu}></i>
  //       </div>
  //     );
  //   }

  //   if (url && url?.substring(0, 2) != "la") {
  //     icon = <img className={`icon navIcon-${nesting}`} src={url} draggable={false} onContextMenu={onContextMenu} />;
  //   }
  // }

  // Die Auswahl erfolgt über den Fokus. Zum Referenzziel navigiert nur der Navigate-to-Button.
  function onClick(ev: any) {
    return;

    if (obj.treeViewModel.isSelectable) {
      if (treeContext?.isGlobalSelectionTree) {
        state.selectionManager.setSelectedObjectWithoutNavigatorUpdate(obj);
      } else {
        state.selectionManager.selectedObject = obj;
      }
    } else {
      state.selectionManager.selectedObject = undefined;
    }
  }

  // Toggles the expand/collapse state when the caret icon is clicked.
  function clickExpandCollapse(ev: any) {
    ev.preventDefault();
    ev.stopPropagation();
    onClick(ev);
    setIsExpanded(props.navigatorNode?.isPartiallyExpanded ? true : !isExpanded);
  }

  // Opens the global context menu for the domain object at the pointer position.
  function onContextMenu(ev: any) {
    state.contextMenuManager.show(ev, obj);
  }

  // Delegates to the tree view model's double-click handler.
  // If the model does not consume the event, the node is expanded.
  // A short timeout clears any text selection that may have been created by the double-click.
  function onDoubleClick(ev: any) {
    ev.stopPropagation();

    const handledByC1 = zoomC1NavigatorObject(obj);

    if (!handledByC1 && !obj.treeViewModel.handleDoubleClick()) {
      //expand it if is not expanded
      setIsExpanded(true);
    } else {
      // double click is handled by item
      // normaly be starting a "document open"
    }

    setTimeout(() => {
      // remove selection
      (window as any).getSelection()?.removeAllRanges();
      (document.activeElement as any)?.blur();
    }, 100);
  }

  // Returns the first action-button icon element inside the flyout menu, if present.
  function firstMenuButton() {
    return elmRef.current?.querySelector(".treeMenuButtons .treeMenuIcon") as HTMLElement; // no itx
  }

  // Shows the flyout button menu and highlights the row when hovering the invisible
  // "click first button" hit area on the left edge of the menu zone.
  function onMouseEnterFirstMenuButton(e: any) {
    if (panelResizeActive) {
      return;
    }

    const firstButton = firstMenuButton();
    if (firstButton) {
      const treeMenuButtons = elmRef.current?.querySelector(".treeMenuButtons") as HTMLElement; // no itx
      if (treeMenuButtons && !treeContext?.isGlobalSelectionTree) {
        adjustMenu(elmRef.current?.querySelector(".treeMenuButtonsWrapper")); // no itx
        treeMenuButtons.style.visibility = "visible";
      }

      if (elmRef.current) {
        elmRef.current.classList.add("firstButtonActive");
        elmRef.current.classList.remove("firstButtonInactive");
      }
    }
  }

  // Keep the shared highlight when moving from the hit area into its flyout menu.
  function onMouseLeaveFirstMenuButton(e: any) {
    const menu = elmRef.current?.querySelector(".treeMenuButtons"); // no itx
    if (e.relatedTarget instanceof Node && menu?.contains(e.relatedTarget)) {
      return;
    }

    const firstButton = firstMenuButton();
    if (firstButton) {
      firstButton.style.backgroundColor = "";

      if (elmRef.current) {
        elmRef.current?.classList.remove("firstButtonActive");
        elmRef.current.classList.add("firstButtonInactive");
      }
    }
  }

  // Programmatically clicks the first action button — used by the invisible hit area
  // so the user can trigger the primary action without opening the full flyout.
  function onClickFirstMenuButton(e: any) {
    e.stopPropagation();
    e.preventDefault();

    const firstButton = firstMenuButton();
    firstButton?.click();
  }

  // Makes the flyout button menu visible when the pointer enters the node row.
  // A CSS transition (fullVisible class) fades the menu in after a short delay.
  function onMouseEnterContent(e: any) {
    if (panelResizeActive) {
      return;
    }

    const treeMenuButtons = elmRef.current?.querySelector(".treeMenuButtons") as HTMLElement; // no itx
    if (treeMenuButtons) {
      adjustMenu(elmRef.current?.querySelector(".treeMenuButtonsWrapper")); // no itx
      showTreeMenu(treeMenuButtons);
    }
  }

  // Hides the flyout button menu when the pointer leaves the node row,
  // unless the pointer is currently inside the floating button dialog itself.
  function onMouseLeaveOnContent(e: any) {
    if (isNavigatorRowGrab(e.relatedTarget, elmRef.current)) return;
    const treeMenuButtons = elmRef.current?.querySelector(".treeMenuButtons") as HTMLElement; // no itx
    if (treeMenuButtons) {
      if (!mouseIsInsideButtonsDialog.current) {
        // treeMenuButtons.style.removeProperty('visibility');
        hideTreeMenu(treeMenuButtons);
      }
    }
  }
  // Drag-and-drop handlers: the tree view model decides whether a drop is allowed.
  // preventDefault() in onDragOver enables the browser's drop target behaviour.
  function onDragEnter(e: any) {
    e.preventDefault();
  }

  function onDragOver(e: any) {
    if (obj.treeViewModel.allowDrop(e)) {
      e.preventDefault();
    }
  }

  function onDragLeave(e: any) {
    e.preventDefault();
  }

  // Indent step per nesting level: 10 px on desktop, 5 px on mobile.
  let w = 10;
  if (Browser.isMobile()) {
    w = 5;
  }

  // Total left-padding for this node based on its depth in the tree.
  const indentWidth = `${(nesting - 1) * w}px`;

  const selected = obj.selected || obj.highlighted;

  // Build the CSS class string for the row element: base class plus conditional modifiers.
  let cn = "dataline"; // no itx
  if (selected) {
    cn += " selected"; // no itx
  }
  if (isExpanded) {
    cn += " expanded"; // no itx
  }

  // logger.log(`mwe1 render ${obj.name}`);
  // check if editeble (selected and given)
  // Inline editing is allowed only when: the node is selected, the focus delay has passed,
  // and the tree view model explicitly permits editing for this object.
  let disableToEditByTreeviewModel = true;
  if (obj.treeViewModel.getDisableEdit) {
    if (obj.selected && !obj.treeViewModel.getDisableEdit()) {
      disableToEditByTreeviewModel = false;
    }
  }

  // Leere Tags bekommen nur bei ausgewählten, bearbeitbaren Zeilen ein Feld.
  // Ohne tagElem entfällt auch das umgebende navTag-div vollständig.
  let tagElem: any = null;

  // Whether the tree view model requires a tag to be displayed for this object type.
  const showTag = treeContext.showTags ?? obj.treeViewModel.getTagNeeded();

  // Name element — an editable field shown when the object has a name or is selected.
  let nameElem: any = null;
  // fullTitle overrides both tag and name with a single read-only string (e.g. for virtual nodes).
  const fullTitle = obj.treeViewModel.getFullTitle(isExpanded);
  const tag = obj.treeViewModel.getTag();

  if (showTag && (tag != "" || (obj.selected && !disableToEditByTreeviewModel && !obj.treeViewModel.getDisableEditTag()))) {
    const tagStyle = obj.treeViewModel.getTagStyle();
    tagElem = (
      <EditableText
        obj={obj}
        property="tag"
        propertyType="text"
        style={{ pointerEvents: "auto", ...tagStyle }}
        upperCase={true}
        disableEdit={
          focusDelayPending.current ||
          disableToEditByTreeviewModel ||
          obj.treeViewModel.getDisableEditTag() ||
          !obj.treeViewModel.isFocusedInNavigator
        }
        maxCharacters={state.maxCharLimit.tag}
        checklistValueDouble={obj.getAllTagsFromSameTypeWithoutOwn.bind(obj)}
        emptyForbidden={obj.emptyTagIsForbidden()}
        placeholder={noItx("Tag")}
        showToastX={showToast}
        onEnterkey={() => {
          if (elmRef.current) {
            elmRef.current.focus();
          }
        }}
      />
    );
  }

  if (fullTitle) {
    nameElem = <div>{fullTitle}</div>;
  } else {
    // check if tag needed or not available

    if (!obj.selected && obj.displayName.trim() == "") {
      nameElem = undefined;
    } else {
      if ((obj.selected || obj.displayName != "") && obj.treeViewModel.getNameNeeded()) {
        nameElem = (
          <EditableText
            obj={obj}
            property="displayName" // no itx
            propertyType="text"
            style={{}}
            disableEdit={
              focusDelayPending.current ||
              disableToEditByTreeviewModel ||
              obj.treeViewModel.getDisableEditName() ||
              !obj.treeViewModel.isFocusedInNavigator
            }
            maxCharacters={state.maxCharLimit.text}
            onCancel={() => {
              elmRef.current?.focus();
              if (elmRef.current) {
                elmRef.current.classList.remove("editActive"); // no itx
              }
            }}
            placeholder={state.translate("name", "Name")}
            onEnterkey={() => {
              if (elmRef.current) {
                elmRef.current.focus();
              }
            }}
          />
        );
      }
    }
  }

  const hasChildren = treeContext.childrenProvider
    ? treeContext.childrenProvider(node).length > 0
    : obj.treeViewModel.isFolder && obj.treeViewModel.getHasChildren(node);

  const prefix: any = obj.treeViewModel.getTitlePrefix();
  const postfix = <>{obj.treeViewModel.getTitlePostfix()}{treeContext.renderPostfix?.(node)}</>;

  let selectionCheckbox: any | undefined;

  if (treeContext?.enableSelection) {
    selectionCheckbox = (
      <input
        className="multiSelect"
        key={`multiSelect-${obj.id}-${obj.multiSelected}`}
        type="checkbox"
        checked={obj.multiSelected}
        onChange={(ev) => {
          obj.multiSelected = ev.currentTarget.checked;
          redraw();
        }}
      />
    );
  }

  // Converts an ErrorItems map to a plain HTML string for use in tooltip attributes.
  function errorsAsHtmlString(errors: ErrorItems | undefined): string | undefined {
    let res = "";

    for (const i in errors) {
      if (errors[i] != undefined) {
        const sError = errors[i];
        res += itx("errors." + i, i) + "<br/>" + sError;
      }
    }

    return res ? res : undefined;
  }

  // Builds a JSX list of error entries, each showing a clickable object reference
  // and the translated error key — used in the error detail dialog.
  function errorsAsElement(allErrorObjects: Array<BaseModel>): JSX.Element {
    const elms: any = [];

    allErrorObjects.forEach((obj1) => {
      for (const i in obj1.errors) {
        elms.push(
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            <ObjectReference obj={obj1} relative={obj} useAllwaysMaxSpace={true} useSimpleFulltag={true} />{" "}
            {itx("errors." + i, i)}
          </div>,
        );
      }
    });

    return <div style={{ display: "flex", flexDirection: "column" }}>{elms}</div>;
  }

  // Returns true if the node label matches the current tree search string (case-insensitive).
  // Used to bold matching nodes in the search results.
  function isValidSearchNode(name: any) {
    if (state.treeSearchString.value == undefined || state.treeSearchString.value == "" || name == "") return false;
    else return name.toLowerCase().includes(state.treeSearchString.value.toLowerCase());
  }

  let navTagClass = `navTag navTag-${nesting}`;

  if (!nameElem && !postfix) {
    //
    // if this row dosnt have a name content,
    // don' t adjust the the ColumSize of the tag to enable the "firstButton-Overlay"
    // close the the end of the tag
    //
    navTagClass = "navTag"; // no itx
  }

  // Determine the caret icon class:
  // - isWaiting (spinner) while children are loading asynchronously
  // - isExpanded / isCollapsed based on current state
  // - hasNoChildren when the object is a leaf
  let caretClassName = "caret las"; // no itx

  if (obj.treeViewModel.getWaitingForChildren()) {
    caretClassName += " isWaiting la-spin"; // no itx
  } else if (props.navigatorNode?.isPartiallyExpanded) {
    // The diamond replaces the expand arrow; combining both states also rotates its glyph.
    caretClassName += " isPartiallyExpanded";
  } else if (hasChildren) {
    if (isExpanded) {
      caretClassName += " isExpanded"; // no itx
    } else {
      caretClassName += " isCollapsed"; // no itx
    }
  } else {
    caretClassName += " hasNoChildren"; // no itx
  }

  const showNavigatorDiagnostics = state.settings.applicationMode !== ApplicationMode.ConceptOne;
  if (showNavigatorDiagnostics && obj.errorInChild) {
    caretClassName += " hasError";
  }
  // if (obj.errorInChild) {
  //   caretClassName = "fa-solid " + caretClassName; // no itx
  // } bringt nix

  let buttonsDialog: any = undefined;

  buttonsDialog = ButtonsDialog({ node: node, obj: obj, treeContext: treeContext });

  cn += " firstButtonInactive";

  let modifiedStatusElm: any = undefined;
  let modifiedColor: string | undefined;

  // Error badge: shown as a red alert icon with a tooltip listing all errors on the object.
  if (showNavigatorDiagnostics && obj.hasErrors) {
    const sError = errorsAsHtmlString(obj.errors) || "";
    modifiedStatusElm = (
      <img
        id="errorIcon"
        src="/images/alert.png" // no itx
        style={{
          position: "absolute",
          left: "0px",
          top: "0px",
          marginRight: "5px",
          height: "25px",
          width: "25px",
          flexShrink: "0",
        }}
        data-tooltip-id="tv-tooltip"
        data-tooltip-html={sError}
        data-tooltip-place="top-end" // no itx
        data-tooltip-variant="error" // no itx
      ></img>
    );
  }

  // Modified-status indicator: a small coloured dot shown when the global
  // "show modified status" option is active. Colour codes:
  //   orange  → object has unsaved changes (dirty)
  //   green   → object was newly created and not yet persisted
  //   blue border → a child object has unsaved changes
  if (obj.isPersistent && state.showModifiedStatus) {
    modifiedColor = "grey";

    let s = "";
    if (obj.dirtyChilds) {
      s = itx("navigationTreeNode.underObjIsBeenChanged", "Unter-Objekte wurden geändert");
    }

    if (obj.isDirty && !obj.isDeleted) {
      if (s) {
        s += "<br/> " + itx("navigationTreeNode.andThe", "und das ");
      }
      s += itx("navigationTreeNode.objChanged", "Objekt wurde geändert");
    } else if (!obj.isLoadedFromPersistence) {
      if (s) {
        s += "<br/> " + itx("navigationTreeNode.andThe", "und das ");
      }
      s += itx("navigationTreeNode.objCreatedNew", "Objekt wurde neu erzeugt");
    }

    if (isDevelopmentEnvironment() && obj.modifyPropertyHistory) {
      obj.modifyPropertyHistory.forEach((entry) => {
        s += `<br/> ${entry.name}: ${entry.newValue} `;
      });
    }

    let backgroundColor = "transparent";
    if (obj.isDirty) {
      backgroundColor = "orange";
    }
    if (!obj.isLoadedFromPersistence) {
      backgroundColor = "green";
    }

    modifiedStatusElm = (
      <div
        key={"modifiedStatusElm" + obj.id}
        style={{
          marginRight: "5px",
          height: "15px",
          width: "15px",
          borderRadius: "20px",
          background: backgroundColor,
          border: obj.dirtyChilds ? "2px solid blue" : "2px solid transparent", // no itx
        }}
        data-tooltip-id="tv-tooltip"
        data-tooltip-html={s}
        data-tooltip-place="right"
      ></div>
    );
  }

  // Project-comparison mode: overlay a coloured dot indicating the diff status
  // of this node relative to the other project being compared.
  // Colour codes: orange = modified, red = deleted, green = new, blue border = child modified.
  if (window.compareState) {
    let background = "transparent";
    let border = "2px solid transparent"; // no itx

    let s = "";

    if (obj.compareStatus & enumCompareStatus.childModified) {
      border = "2px solid blue";
      s = itx("navigationTreeNode.underObjIsBeenChanged", "Unter-Objekte wurden geändert");
    }

    if (obj.compareStatus & enumCompareStatus.modified) {
      background = "orange";
      if (s) {
        s += "<br/> " + itx("navigationTreeNode.andThe", "und das ");
      }
      s += itx("navigationTreeNode.objChanged", "Objekt wurde geändert");
    }

    if (node.isDeleted) {
      background = "red";
      s += itx("navigationTreeNode.objDeletedInProject", "Objekt wurde in aktuellem Projekt gelöscht");
    }

    if (node.hasCompareStatus(enumCompareStatus.deletedInOtherProject)) {
      border = "2px solid red";
      s += itx("navigationTreeNode.objDeletedInOtherProject", "Objekt wurde in anderem Projekt gelöscht");
    }

    if (node.hasCompareStatus(enumCompareStatus.newInCurrentProject)) {
      background = "green";
      s += itx("navigationTreeNode.objAddedInProject", "Objekt wurde in aktuellem Projekt hinzugefügt");
    }

    if (node.hasCompareStatus(enumCompareStatus.newInOtherProject)) {
      border = "2px solid green";
      s += itx("navigationTreeNode.objAddedInOtherProject", "Objekt wurde in anderem Projekt hinzugefügt");
    }

    modifiedStatusElm = (
      <div
        key={"modifiedStatusElm" + obj.id}
        style={{
          marginRight: "5px",
          height: "15px",
          width: "15px",
          borderRadius: "20px",
          background: background,
          border: border,
          flexShrink: "0",
        }}
        data-tooltip-id="tv-tooltip"
        data-tooltip-html={s}
        data-tooltip-place="right"
      ></div>
    );
  }

  const errorInChild = showNavigatorDiagnostics && obj.errorInChild;

  console.log("render NavigationTreeNodeContent", obj.tag, obj.selected, obj.treeViewModel.isFocusedInNavigator);

  return (
    <div
      ref={elmRef}
      data-testid={treeViewNodeId(treeContext.treename, (counter.current++).toString())}
      id={treeViewNodeId(treeContext.treename, obj.id, treeContext.navigatorInstance)}
      data-connection-object-id={obj.id}
      key={treeViewNodeId(treeContext.treename, obj.id, treeContext.navigatorInstance)}
      className={cn}
      tabIndex={1}
      onDrop={(ev) => obj.treeViewModel.onDrop(ev)}
      onMouseEnter={onMouseEnterContent}
      onMouseLeave={onMouseLeaveOnContent}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      onKeyDown={event => {
        if (!obj.treeViewModel.isSelectable && (event.key === "Delete" || event.key === "Backspace")) event.preventDefault();
      }}
      onBlur={(event) => {
        const currentTarget = event.currentTarget;
        const relatedTarget = event.relatedTarget;

        if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
          // Fokus geht wirklich weg von dem gesamten Bereich
          setFixedOpenIfIsTransientOpen(obj);
          state.selectionManager.focusedObjectInNavigator = undefined;
          focusDelayPending.current = true;
          redraw();
        } else {
          // Fokus bleibt innerhalb des Bereichs (z. B. zu einem Kind)
          // console.log('Focus moved to a child element');
        }
      }}
      onFocus={(event) => {
        if (state.selectionManager.focusedObjectInNavigator == obj) {
          //
          // already focused, do nothing
          // to enable save focusing of editfields
          //
          return;
        }

        const currentTarget = event.currentTarget;
        const relatedTarget = event.relatedTarget;

        focusDelayPending.current = true;
        setTimeout(() => {
          focusDelayPending.current = false;
          redraw();
        }, 500);

        if (obj.treeViewModel.isSelectable && !treeContext.onSelectObject?.(obj)) {
          state.selectionManager.setSelectedObjectWithoutNavigatorUpdate(obj);
        }
        state.selectionManager.focusedObjectInNavigator = obj;

        if (elmRef.current) {
          elmRef.current.classList.add("editActive"); // no itx
        }
        if (!treeContext?.isGlobalSelectionTree) {
          adjustMenu(elmRef.current?.querySelector(".treeMenuButtonsWrapper")); // no itx
        }

        redraw();
      }}
      onDragStart={obj.treeViewModel.allowDrag() ? dragStart : undefined}
      draggable={obj.treeViewModel.allowDrag()}
      onClick={(ev) => onClick(ev)}
      style={{
        fontWeight: isValidSearchNode(obj.displayName || obj.tag) ? "bold" : "normal", // no itx
        position: "relative",
        borderTopRightRadius: buttonsDialog ? undefined : "20px",
        borderBottomRightRadius: buttonsDialog ? undefined : "20px",
      }}
    >
      <div key={"flexShrink0" + indentWidth + obj.id} style={{ flexShrink: "0", width: indentWidth }}></div>

      {obj.treeViewModel.isFolder && (
        <div
          className={caretClassName}
          id={`caret-${obj.id}`}
          onClick={clickExpandCollapse}
          onContextMenu={onContextMenu}
          data-tooltip-id={props.navigatorNode?.isPartiallyExpanded || errorInChild ? "tv-tooltip" : undefined}
          data-tooltip-html={
            props.navigatorNode?.isPartiallyExpanded
              ? itx(
                  "NavigationTreeNode.teilweiseAufgeklapptNur",
                  "Teilweise aufgeklappt: Nur der ausgewählte Pfad ist sichtbar. Klicken, um alle Kinder anzuzeigen.",
                )
              : errorInChild
                ? itx("navigationTreeNode.errorInSubObjects", "Fehler in Unterobjekten")
                : undefined
          }
          data-tooltip-place={errorInChild ? "top-end" : undefined} // no itx
          data-tooltip-variant={!props.navigatorNode?.isPartiallyExpanded && errorInChild ? "error" : undefined} // no itx
        ></div>
      )}

      {modifiedStatusElm}

      {selectionCheckbox}
      {!obj.treeViewModel.isFolder && (
        <div key={"onContextMenu" + obj.id + "&nbsp"} onContextMenu={onContextMenu}>
          &nbsp; &nbsp; &nbsp;
        </div>
      )}

      {node.isDeleted && (
        <div
          key={"node.isDeleted" + obj.id}
          style={{
            position: "absolute",
            color: "red",
            left: indentWidth,
            top: "15px",
            backgroundColor: "red",
            height: "2px",
            width: "100%",
          }}
        />
      )}

      {icon}

      <div
        style={{
          flexShrink: treeContext?.isGlobalSelectionTree ? "1" : "0",
          whiteSpace: "nowrap",
          flexGrow: "0",
          display: "flex",
          flexDirection: "row",
          textOverflow: "ellipsis",
          overflow: "hidden",
        }}
        onClick={(ev) => onClick(ev)}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
      >
        <div
          style={{
            position: "relative",
            whiteSpace: "nowrap",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          {prefix}
          {tagElem && (
            <div
              className={navTagClass}
              style={{
                fontWeight: "bolder",
              }}
            >
              {tagElem}
            </div>
          )}
          {tag != "" && obj.displayName != "" && showTag && <div>&nbsp;</div>}
          {nameElem} {postfix}
        </div>
      </div>
      {flyoutMenus && buttonsDialog && <div key={"flexgrow1" + obj.id} style={{ flexGrow: "1" }} />}

      {flyoutMenus && buttonsDialog && !treeContext?.isGlobalSelectionTree && (
        <div
          style={{ maxWidth: "100px" }}
          className="clickFirstButton"
          onMouseDown={(event) => {
            if (event.button === 0) event.preventDefault();
          }}
          onMouseEnter={onMouseEnterFirstMenuButton}
          onMouseLeave={onMouseLeaveFirstMenuButton}
          onClick={onClickFirstMenuButton}
        ></div>
      )}

      {buttonsDialog}
    </div>
  );
}

/**
 * Public wrapper component exported as the default.
 * Displays the navigator node's expansion and child list. The Commander
 * retains its local expansion state until it adopts a navigator model.
 * Wrapped inside `withErrorBoundary` via `NavigationTreeNodeContentWithErrorBoundary`
 * to isolate rendering errors to individual rows.
 */
export default function NavigationTreeNode(props: {
  navigatorNode?: NavigatorNode;
  treeContext: TreeContext;
  nesting: number;
  node: TreeNodeInfo;
  parentNodeInfo: TreeNodeInfo;
  allignAllColumns?: (parentObj: BaseModel, root?: HTMLElement | null) => void;
  flyoutMenus: boolean;
}) {
  const { node, nesting, treeContext, allignAllColumns, flyoutMenus } = props;
  const obj = node.obj;

  const [legacyExpanded, setLegacyExpanded] = useState<boolean>(false);
  const rowElement = useRef<HTMLLIElement>(null);
  useDeletedNodeAnimation(rowElement, obj);
  useOnChangeWatcher(props.navigatorNode);
  const isExpanded = props.navigatorNode?.isOpen ?? legacyExpanded;
  const setIsExpanded = (expanded: boolean) => {
    if (props.navigatorNode) props.navigatorNode.setExpanded(expanded);
    else setLegacyExpanded(expanded);
  };

  return (
    <li
      ref={rowElement}
      data-navigator-reference={node.isReference || ["BaseModelReference", "DeviceReference", "FuseConsumerReference"].includes(obj.className) ? obj.id : undefined}
      data-deleted={obj.isDeleted || undefined}
      key={treeViewNodeId(treeContext?.treename, obj.id, treeContext?.navigatorInstance)}
      style={{ minHeight: "32px", textDecoration: obj.isDeleted ? "line-through" : undefined }}
    >
      <NavigationTreeNodeContentWithErrorBoundary
        {...props}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        allignAllColumns={allignAllColumns}
        flyoutMenus={flyoutMenus}
      />
      {isExpanded && (
        <NavigationTreeNodes
          navigatorNode={props.navigatorNode}
          parent={obj}
          key={"ch-" + treeViewNodeId(treeContext.treename, obj.id, treeContext.navigatorInstance)} // no itx
          nesting={nesting + 1}
          parentNodeInfo={node}
          treeContext={treeContext}
          flyoutMenus={flyoutMenus}
        />
      )}
    </li>
  );
}
